import type { EconomicEvent } from "./types";

/** One row per unique impact|currency|name across the loaded corpus */
export type EconomicSeriesCatalogRow = {
  compositeKey: string;
  impact: EconomicEvent["impact"];
  currency: string;
  name: string;
  count: number;
};

/** Same grouping/sort order as footer/settings catalog UI */
export function buildSeriesCatalogRows(
  events: readonly EconomicEvent[]
): EconomicSeriesCatalogRow[] {
  const m = new Map<string, EconomicSeriesCatalogRow>();
  for (const e of events) {
    const compositeKey = `${e.impact}|${e.currency}|${e.name}`;
    const cur = m.get(compositeKey);
    if (cur) {
      cur.count += 1;
    } else {
      m.set(compositeKey, {
        compositeKey,
        impact: e.impact,
        currency: e.currency,
        name: e.name,
        count: 1,
      });
    }
  }
  return Array.from(m.values()).sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    if (a.impact !== b.impact)
      return String(a.impact).localeCompare(String(b.impact));
    if (a.currency !== b.currency)
      return a.currency.localeCompare(b.currency);
    return a.name.localeCompare(b.name);
  });
}

export function filterCatalogByCurrency<T extends EconomicSeriesCatalogRow>(
  rows: readonly T[],
  currency: string
): T[] {
  const c = currency.trim().toUpperCase();
  return rows.filter((r) => r.currency.toUpperCase() === c);
}

export function filterCatalogByNameSubstring<T extends EconomicSeriesCatalogRow>(
  rows: readonly T[],
  nameQuery: string
): T[] {
  const t = nameQuery.trim().toLowerCase();
  if (!t) return [...rows];
  return rows.filter((r) => r.name.toLowerCase().includes(t));
}

export function filterCatalogByImpact<T extends EconomicSeriesCatalogRow>(
  rows: readonly T[],
  impact: EconomicEvent["impact"]
): T[] {
  return rows.filter((r) => r.impact === impact);
}
